import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Grid } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { trackEvent } from "../../utils/analytics";
import { getVisibleWeekOffset } from "../../utils/dateUtils";
import { StaffingDateTypeSelector } from "../staffing/StaffingDateTypeSelector";
import Style from "./DateSlider.module.scss";

const formatDateRange = (
	startDate: DateTime | undefined,
	endDate: DateTime | undefined,
	locale: string,
	// Hospital demo → abbreviated month (Jul 2026); everyone else → full month (July 2026).
	isHospitalDemo: boolean,
) => {
	if (startDate && endDate) {
		const startMoment = startDate.setLocale(locale);
		const endMoment = endDate.setLocale(locale);
		const monthFormat = isHospitalDemo ? "MMM" : "MMMM";

		if (startMoment.month === endMoment.month) {
			// Same month
			return startMoment.toFormat(`${monthFormat} yyyy`);
		}
		// Different months
		return `${startMoment.toFormat(monthFormat)} - ${endMoment.toFormat(`${monthFormat} yyyy`)}`;
	}
	return "";
};

type DateSliderProps = { isFullDates?: boolean; isNoDates?: boolean; hideArrows?: boolean; fromWeeklyStaffing?: boolean };

export const DateSlider: FC<DateSliderProps> = ({ isFullDates, isNoDates, hideArrows, fromWeeklyStaffing: isFromWeeklyStaffing }) => {
	const { i18n } = useTranslation();
	// Only hospital demos (Hospital type + inhouse2).
	const { hospitalData, timezone, getWeekIndexByDate, chosenDepartment, isHospitalView: isHospitalDemoView } = useHospitalData();
	const { weekIndex, setWeekIndex, viewStartDay } = useWeekIndex();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const maxWeekIndex = hospitalData.weeks.length - 1;
	const isRTL = i18n.dir() === "rtl";
	const location = useLocation();
	const navigate = useNavigate();
	const isOnStaffingPage = location.pathname.includes("/staffing") && !location.pathname.includes("/staffing/monthly");

	const handlePrevClick = useCallback(() => {
		if (weekIndex > 0) {
			const newWeekIndex = weekIndex - 1;
			setWeekIndex(newWeekIndex);
			const weekStartDate = hospitalData?.weeks[newWeekIndex]?.weeklyShifts[0]?.days[0]?.date;

			// `history.replaceState` instead of `navigate({ replace: true })` avoids a full RouterProvider
			// re-render (~300ms) for a cosmetic URL change.
			if (isOnStaffingPage && weekStartDate && chosenDepartment?.department_id) {
				const formattedDate = weekStartDate.toFormat("yyyy-MM-dd");
				window.history.replaceState(null, "", `/${chosenDepartment.department_id}/staffing/${formattedDate}`);
			}

			trackEvent("previous_week_clicked", {
				previous_week_index: weekIndex,
				new_week_index: newWeekIndex,
				week_start_date: weekStartDate?.toFormat("yyyy-MM-dd"),
				week_end_date: hospitalData?.weeks[newWeekIndex]?.weeklyShifts[0]?.days[6]?.date?.toFormat("yyyy-MM-dd"),
			});
		}
	}, [weekIndex, setWeekIndex, hospitalData, isOnStaffingPage, chosenDepartment]);

	const handleNextClick = useCallback(() => {
		if (weekIndex < maxWeekIndex) {
			const newWeekIndex = weekIndex + 1;
			setWeekIndex(newWeekIndex);
			const weekStartDate = hospitalData?.weeks[newWeekIndex]?.weeklyShifts[0]?.days[0]?.date;

			if (isOnStaffingPage && weekStartDate && chosenDepartment?.department_id) {
				const formattedDate = weekStartDate.toFormat("yyyy-MM-dd");
				window.history.replaceState(null, "", `/${chosenDepartment.department_id}/staffing/${formattedDate}`);
			}

			trackEvent("next_week_clicked", {
				previous_week_index: weekIndex,
				new_week_index: newWeekIndex,
				week_start_date: weekStartDate?.toFormat("yyyy-MM-dd"),
				week_end_date: hospitalData?.weeks[newWeekIndex]?.weeklyShifts[0]?.days[6]?.date?.toFormat("yyyy-MM-dd"),
			});
		}
	}, [weekIndex, maxWeekIndex, setWeekIndex, hospitalData, isOnStaffingPage, chosenDepartment]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement;
			const isInputElement = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.contentEditable === "true";

			if (isInputElement) return;

			switch (event.key) {
				case "ArrowLeft":
					handlePrevClick();
					break;
				case "ArrowRight":
					handleNextClick();
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handlePrevClick, handleNextClick]);

	const handleResetWeekIndex = useCallback(() => {
		const now = DateTime.now().setZone(timezone);
		const dataWeekIndex = getWeekIndexByDate(now);
		const todayPosInDataWeek = now.weekdayISO % 7;
		const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;
		const targetWeekIndex = todayPosInDataWeek < offset ? Math.max(0, dataWeekIndex - 1) : dataWeekIndex;
		setWeekIndex(targetWeekIndex);
		trackEvent("reset_week_index", {
			week_index_reset: targetWeekIndex,
			current_date: now.toFormat("yyyy-MM-dd"),
		});
	}, [getWeekIndexByDate, setWeekIndex, timezone, viewStartDay, isDynamicWeekStartEnabled]);

	const formattedDateRange = useMemo(() => {
		return isFullDates
			? formatDateRange(
					hospitalData.weeks.at(0)?.weeklyShifts.at(0)?.days.at(0)?.date,
					hospitalData?.weeks.at(-1)?.weeklyShifts.at(0)?.days.at(-1)?.date,
					i18n.language,
					isHospitalDemoView,
				)
			: formatDateRange(
					hospitalData.weeks[weekIndex]?.weeklyShifts.at(0)?.days.at(0)?.date,
					hospitalData.weeks[weekIndex]?.weeklyShifts.at(0)?.days.at(-1)?.date,
					i18n.language,
					isHospitalDemoView,
				);
	}, [isFullDates, hospitalData, weekIndex, i18n.language, isHospitalDemoView]);

	const currentWeekStartDate = useMemo(() => hospitalData.weeks[weekIndex]?.weeklyShifts.at(0)?.days.at(0)?.date, [hospitalData, weekIndex]);

	const { minDate, maxDate } = useMemo(() => {
		const firstDate = hospitalData.weeks.at(0)?.weeklyShifts.at(0)?.days.at(0)?.date;
		const lastDate = hospitalData.weeks.at(-1)?.weeklyShifts.at(0)?.days.at(-1)?.date;
		return { minDate: firstDate, maxDate: lastDate };
	}, [hospitalData]);

	const handleDateSelected = useCallback(
		(selectedDate: DateTime) => {
			const targetWeekIndex = getWeekIndexByDate(selectedDate);
			setWeekIndex(targetWeekIndex);

			const weekStartDate = hospitalData?.weeks.at(targetWeekIndex)?.weeklyShifts[0]?.days[0]?.date;
			if (isOnStaffingPage && weekStartDate && chosenDepartment?.department_id) {
				const formattedDate = weekStartDate.toFormat("yyyy-MM-dd");
				navigate(`/${chosenDepartment.department_id}/staffing/${formattedDate}`);
			}

			trackEvent("weekly_date_picker_date_selected", {
				selected_date: selectedDate.toFormat("yyyy-MM-dd"),
				target_week_index: targetWeekIndex,
			});
		},
		[getWeekIndexByDate, setWeekIndex, hospitalData, isOnStaffingPage, chosenDepartment, navigate],
	);

	return isFromWeeklyStaffing ? (
		<StaffingDateTypeSelector
			// Only this branch: it is the weekly Staffing route's date control, which is what the
			// walkthrough step describes. The Day Schedule and monthly renders of the same selector
			// deliberately carry no anchor.
			data-annie-target="staffing-date-range"
			currentDateText={formattedDateRange}
			initialPageType={"WEEKLY"}
			setWeekIndex={setWeekIndex}
			weekIndex={weekIndex}
			onNextClick={isRTL ? handlePrevClick : handleNextClick}
			onPreviousClick={isRTL ? handleNextClick : handlePrevClick}
			onResetButtonClick={handleResetWeekIndex}
			currentDate={currentWeekStartDate}
			onDateSelected={handleDateSelected}
			minDate={minDate}
			maxDate={maxDate}
		/>
	) : (
		<div className={Style.parentContainer}>
			{!isNoDates && (
				<div className={Style.container}>
					<Grid
						container
						sx={{
							// Hospital demo centers the arrows around the label (< Aug 2026 >);
							// everyone else keeps the original split (arrows left, label right).
							justifyContent: isHospitalDemoView ? "center" : "space-between",
							alignItems: "center",
							flexWrap: "nowrap",
							gap: isHospitalDemoView ? "8px" : 0,
						}}
					>
						<Grid>
							<div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
								{!isFullDates && hospitalData.weeks.length > 1 && !hideArrows && (
									<>
										{isRTL ? (
											// In RTL mode, next becomes previous and vice versa
											<ArrowForwardIosIcon
												onClick={handlePrevClick}
												width="17px"
												sx={{
													color: "primary.main",
													fontSize: "20px",
													cursor: weekIndex === 0 ? "default" : "pointer",
												}}
												style={{ opacity: weekIndex === 0 ? 0.5 : 1 }}
											/>
										) : (
											<ArrowBackIosIcon
												onClick={handlePrevClick}
												width="17px"
												sx={{
													color: "primary.main",
													fontSize: "20px",
													cursor: weekIndex === 0 ? "default" : "pointer",
												}}
												style={{ opacity: weekIndex === 0 ? 0.5 : 1 }}
											/>
										)}

										{/* Hospital demo puts the next arrow AFTER the label (< Aug 2026 >),
										    so render it here only for non-demo; the demo one lives past the label. */}
										{!isHospitalDemoView &&
											(isRTL ? (
												<ArrowBackIosIcon
													width="17px"
													onClick={handleNextClick}
													sx={{
														color: "primary.main",
														fontSize: "20px",
														cursor: weekIndex === maxWeekIndex ? "default" : "pointer",
													}}
													style={{
														opacity: weekIndex === maxWeekIndex ? 0.5 : 1,
													}}
												/>
											) : (
												<ArrowForwardIosIcon
													width="17px"
													onClick={handleNextClick}
													sx={{
														color: "primary.main",
														fontSize: "20px",
														cursor: weekIndex === maxWeekIndex ? "default" : "pointer",
													}}
													style={{
														opacity: weekIndex === maxWeekIndex ? 0.5 : 1,
													}}
												/>
											))}
									</>
								)}
							</div>
						</Grid>
						<Grid>
							<div className={Style.label} style={{ color: "primary.main" }}>
								<div className={Style.textWrapper}>{formattedDateRange}</div>
							</div>
						</Grid>
						{/* Demo-only: the next arrow sits after the label to form < Aug 2026 >. */}
						{isHospitalDemoView && !isFullDates && hospitalData.weeks.length > 1 && !hideArrows && (
							<Grid>
								<div style={{ display: "flex", alignItems: "center" }}>
									{isRTL ? (
										<ArrowBackIosIcon
											width="17px"
											onClick={handleNextClick}
											sx={{ color: "primary.main", fontSize: "20px", cursor: weekIndex === maxWeekIndex ? "default" : "pointer" }}
											style={{ opacity: weekIndex === maxWeekIndex ? 0.5 : 1 }}
										/>
									) : (
										<ArrowForwardIosIcon
											width="17px"
											onClick={handleNextClick}
											sx={{ color: "primary.main", fontSize: "20px", cursor: weekIndex === maxWeekIndex ? "default" : "pointer" }}
											style={{ opacity: weekIndex === maxWeekIndex ? 0.5 : 1 }}
										/>
									)}
								</div>
							</Grid>
						)}
					</Grid>
				</div>
			)}
		</div>
	);
};

export default DateSlider;
